// src/constants/materials-updated.ts - ZAKTUALIZOWANE CENY I PARAMETRY
import { Material, ProductType } from '@/types/Offer';
import { 
  Layers, 
  Package, 
  Box, 
  Shield, 
  Eye, 
  LightbulbIcon,
  Sparkles,
  Building,
  Boxes
} from 'lucide-react';

// DOMYŚLNE WARUNKI HANDLOWE
export const defaultTerms = {
  deliveryTime: '3-10 dni roboczych',
  deliveryMethod: 'Kurier / odbiór osobisty',
  paymentTerms: 'Przelew 7 dni',
  warranty: 'Produkty niestandardowe nie podlegają zwrotowi',
  validity: '7 dni'
};

// HANDLOWCY
export const salespeople = [
  {
    id: 'DB',
    name: 'Dorota Będkowska',
    email: 'dorota@plexisystem.pl',
    phone: '+48 884 042 109'
  },
  {
    id: 'LS',
    name: 'Łukasz Sikorra',
    email: 'lukasz@plexisystem.pl',
    phone: '+48 884 042 107'
  }
];

// REGIONY DOSTAWY
export const deliveryRegions = [
  { id: 'odbior', name: 'Odbiór osobisty', pricePerKg: 0, minPrice: 0 },
  { id: 'trojmiasto', name: 'Trójmiasto', pricePerKg: 0.5, minPrice: 30 },
  { id: 'pomorskie', name: 'Województwo pomorskie', pricePerKg: 0.8, minPrice: 50 },
  { id: 'polska_polnoc', name: 'Polska północna', pricePerKg: 1.2, minPrice: 80 },
  { id: 'polska_pozostala', name: 'Pozostała Polska', pricePerKg: 1.5, minPrice: 100 }
];

// CENY BAZOWE MATERIAŁÓW (za m² przy 1mm grubości)
// Dokładnie jak w starym kalkulatorze
export const materials: Material[] = [
  {
    id: 'plexi',
    name: 'Plexi bezbarwne',
    basePrice: 30,        // 30 zł za m² przy 1mm grubości
    density: 1190,         // kg/m³
    // NIE używamy fixedThickness - będzie suwak
  },
  {
    id: 'plexi_satyna',
    name: 'Plexi satynowe',
    basePrice: 36,        // 36 zł za m² przy 1mm grubości
    density: 1190,
    // NIE używamy fixedThickness - będzie suwak
  },
  {
    id: 'plexi_opal',
    name: 'Plexi mleczne/opal',
    basePrice: 33,        // 33 zł za m² przy 1mm grubości
    density: 1190,
    // NIE używamy fixedThickness - będzie suwak
  },
  {
    id: 'plexi_color',
    name: 'Plexi kolorowe',
    basePrice: 50,        // Cena bazowa, ale używamy pricePerThickness
    density: 1190,
    // NIE używamy fixedThickness - będzie suwak
    pricePerThickness: {  // Ceny za m² dla konkretnych grubości
      1: 50,     // 1mm = 50 zł/m²
      1.5: 75,   // 1.5mm = 75 zł/m²  
      2: 100,    // 2mm = 100 zł/m²
      3: 150,    // 3mm = 150 zł/m²
      4: 200,    // 4mm = 200 zł/m²
      5: 250,    // 5mm = 250 zł/m²
      6: 300,    // 6mm = 300 zł/m²
      8: 400,    // 8mm = 400 zł/m²
      10: 500,   // 10mm = 500 zł/m²
      12: 600,   // 12mm = 600 zł/m²
      15: 750,   // 15mm = 750 zł/m²
      20: 1000,  // 20mm = 1000 zł/m²
      30: 1500   // 30mm = 1500 zł/m²
    }
  },
  {
    id: 'petg',
    name: 'PET-G',
    basePrice: 28,        // 28 zł za m² przy 1mm grubości
    density: 1270,
    // NIE używamy fixedThickness - będzie suwak
  },
  {
    id: 'poliweg',
    name: 'Poliwęglan',
    basePrice: 40,        // 40 zł za m² przy 1mm grubości
    density: 1200,
    // NIE używamy fixedThickness - będzie suwak
  },
  {
    id: 'dibond',
    name: 'Dibond',
    basePrice: 80,        // 80 zł za m² (stała grubość 3mm)
    density: 1500,
    fixedThickness: [3],  // Tylko 3mm
  },
  {
    id: 'pcv',
    name: 'PCV spienione',
    basePrice: 12,        // 12 zł za m² przy 1mm grubości
    density: 550,
    // NIE używamy fixedThickness - będzie suwak
  }
];

// TYPY PRODUKTÓW Z PARAMETRAMI KALKULACJI
// Dokładnie jak w starym kalkulatorze!
export const productTypes: ProductType[] = [
  {
    id: 'formatka',
    name: 'Formatka',
    icon: Layers,
    waste: 0.05,         // 5% odpadu
    multiplier: 1.3,     // Marża 30%
  },
  {
    id: 'ekspozytor',
    name: 'Ekspozytor',
    icon: Eye,
    waste: 0.08,         // 8% odpadu
    multiplier: 1.8,     // Marża 80%
  },
  {
    id: 'pojemnik',
    name: 'Pojemnik',
    icon: Box,
    waste: 0.08,         // 8% odpadu
    multiplier: 1.5,     // Marża 50%
  },
  {
    id: 'gablota',
    name: 'Gablota',
    icon: Shield,
    waste: 0.1,          // 10% odpadu
    multiplier: 2.0,     // Marża 100%
  },
  {
    id: 'obudowa',
    name: 'Obudowa',
    icon: Package,
    waste: 0.08,         // 8% odpadu
    multiplier: 1.5,     // Marża 50%
  },
  {
    id: 'kaseton',
    name: 'Kaseton',
    icon: LightbulbIcon,
    waste: 0.08,         // 8% odpadu (ale kaseton ma stałą cenę)
    multiplier: 1.0,     // Bez dodatkowej marży (cena 1550/1400 zł/m² już zawiera marżę)
  },
  {
    id: 'ledon',
    name: 'LED-ON',
    icon: Sparkles,
    waste: 0.05,         // 5% odpadu
    multiplier: 1.4,     // Marża 40%
  },
  {
    id: 'impuls',
    name: 'Impuls',
    icon: Building,
    waste: 0.08,         // 8% odpadu
    multiplier: 1.6,     // Marża 60%
  }
];

// TYPY EKSPOZYTORÓW
export const expositorTypes = [
  { 
    id: 'podstawkowy', 
    name: 'Podstawkowy', 
    description: 'Podstawa + tylna ścianka + opcjonalne boki' 
  },
  { 
    id: 'schodkowy', 
    name: 'Schodkowy', 
    description: 'Półki stopniowane, idealne na kosmetyki' 
  },
  { 
    id: 'z_haczykami', 
    name: 'Z haczykami', 
    description: 'Panel z haczykami na produkty' 
  },
  { 
    id: 'wiszacy', 
    name: 'Wiszący', 
    description: 'Montaż na ścianie, kieszenie na ulotki' 
  },
  { 
    id: 'stojak', 
    name: 'Stojak wolnostojący', 
    description: 'Stabilna podstawa, modułowa konstrukcja' 
  },
  { 
    id: 'kosmetyczny', 
    name: 'Kosmetyczny', 
    description: 'Z przegrodami na produkty kosmetyczne' 
  }
];

// GŁÓWNA FUNKCJA KALKULACJI
// Ta sama logika co w starym kalkulatorze!
export const calculatePrice = (
  material: Material,
  thickness: number,
  surface: number,
  productType: ProductType,
  quantity: number = 1
): number => {
  // Dla kasetonu - stała cena za m²
  if (productType.id === 'kaseton') {
    // Kaseton z plexi: 1550 zł/m²
    // Kaseton z dibond: 1400 zł/m²
    const basePrice = material.id === 'dibond' ? 1400 : 1550;
    return basePrice * surface * quantity;
  }

  // Powierzchnia z odpadem
  const surfaceWithWaste = surface * (1 + productType.waste);
  
  // Koszt materiału
  let materialCost;
  
  // Dibond ma stałą cenę 80 zł/m² (nie mnożymy przez grubość)
  if (material.id === 'dibond') {
    materialCost = material.basePrice * surfaceWithWaste;
  } else if (material.id === 'plexi_color' && material.pricePerThickness) {
    // Dla plexi kolorowej - cena zależy od grubości
    const pricePerM2 = material.pricePerThickness[thickness] || (material.basePrice * thickness);
    materialCost = pricePerM2 * surfaceWithWaste;
  } else {
    // Dla pozostałych - cena bazowa × grubość × powierzchnia
    materialCost = material.basePrice * thickness * surfaceWithWaste;
  }
  
  // Minimalne zamówienie 0.1 m² dla pojedynczej sztuki
  if (quantity === 1 && surface < 0.1) {
    materialCost = materialCost * (0.1 / surface);
  }
  
  // Robocizna (33.3% kosztu materiału)
  const laborCost = materialCost * 0.333;
  
  // Cena przed marżą
  const costBeforeMargin = materialCost + laborCost;
  
  // Dodaj marżę
  const unitPrice = costBeforeMargin * productType.multiplier;
  
  return unitPrice * quantity;
};

// WZÓR KALKULACJI - PODSUMOWANIE
/*
1. FORMATKA (płyta cięta na wymiar):
   - Powierzchnia = szerokość × wysokość
   - Odpad: 5%
   - Marża: ×1.3
   - Przykład: 1000×1000mm, plexi 3mm = 163.76 zł

2. EKSPOZYTOR:
   - Powierzchnia = podstawa + tył + boki + półki
   - Odpad: 8%
   - Marża: ×1.8

3. POJEMNIK:
   - Powierzchnia = 2×(przód+tył) + 2×boki + dno + opcjonalne wieko
   - Odpad: 8%
   - Marża: ×1.5

4. GABLOTA:
   - Powierzchnia = 2×(przód+tył) + 2×boki + góra + dół
   - Odpad: 10%
   - Marża: ×2.0

5. OBUDOWA:
   - Powierzchnia = 5 ścian (bez góry) lub 6 ścian
   - Odpad: 8%
   - Marża: ×1.5

6. KASETON:
   - Stała cena: 1550 zł/m² (plexi) lub 1400 zł/m² (dibond)
   - Powierzchnia = szerokość × wysokość frontu

7. LED-ON:
   - Powierzchnia = szerokość × wysokość
   - Odpad: 5%
   - Marża: ×1.4

8. IMPULS:
   - Powierzchnia = tylna ścianka + boki + półki
   - Odpad: 8%
   - Marża: ×1.6
*/

// CENY DODATKÓW (zaktualizowane zgodnie z wymaganiami)
export const ADDON_PRICES = {
  // Grafika
  GRAFIKA_JEDNOSTRONNA: 75,      // zł/m²
  GRAFIKA_DWUSTRONNA: 150,       // zł/m²
  
  // Obróbka
  POLEROWANIE_KRAWEDZI: 9,       // zł/mb
  
  // Okucia
  ZAWIASY: 18,                   // zł/komplet
  ZAMEK: 18,                     // zł/sztuka
  
  // Topery
  TOPPER_Z_GRAFIKA: 180,         // zł/m²
  
  // Litery kasetonowe
  LITERY_PODKLEJANE: 0,          // w cenie (gratis)
  LITERY_ZLICOWANE: 200,         // zł/m²
  LITERY_WYSTAJACE: 400,         // zł/m²
  LITERY_HALO: 500,              // zł/m²
  
  // LED
  LED_STANDARD: 30,              // zł/mb
  LED_COB: 55,                   // zł/mb
  LED_RGB: 80,                   // zł/mb
  ZASILACZ_LED: 80,              // zł/sztuka
  
  // Opakowanie
  KARTON: 8,                     // zł/m²
  
  // Dystanse
  NOZKI: 25,                     // zł/komplet
  
  // Montaż
  TASMA_DWUSTRONNA: 2,           // zł/mb
};